import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, MessageCircle, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, TIER_LABEL, TIER_RANK } from "../lib/api";
import { PostCard } from "../components/feed/PostCard";
import { TipDialog } from "../components/feed/TipDialog";

const FILTERS = [["all", "All"], ["image", "Photos"], ["video", "Videos"], ["locked", "Locked"]];

function ProfileCard({ user, onTip }) {
  const isAdmin = user.role === "admin";
  return (
    <aside data-testid="feed-profile-card" className="self-start lg:sticky lg:top-[100px] border border-[#0E1B3D]/[0.08] bg-[#FFFFFF] overflow-hidden">
      <div className="relative h-36"><img src="/img/hero.jpg" alt="" className="w-full h-full object-cover object-[50%_25%]" /><div className="absolute inset-0 bg-gradient-to-t from-[#FFFFFF] to-transparent" /></div>
      <div className="px-6 pb-7 -mt-10 relative">
        <img src="/img/avatar.jpg" alt="Katie" className="w-20 h-20 rounded-full object-cover ring-2 ring-[#D7263D]" />
        <h2 className="mt-4 font-display text-2xl text-[#0E1B3D]">Katie Razenski</h2>
        <p className="text-sm text-[#4A5878]">{isAdmin ? "You're viewing as the creator" : <>Your tier: <span data-testid="feed-user-tier" className="text-[#D7263D]">{TIER_LABEL[user.tier]}</span></>}</p>
        {!isAdmin && (
          <div className="mt-6 grid gap-2">
            <Link to="/messages" data-testid="feed-messages-link" className="ghost-btn py-3 text-xs tracking-[0.16em] uppercase flex items-center justify-center gap-2"><MessageCircle className="w-4 h-4" /> Message Katie</Link>
            <button data-testid="feed-profile-tip-btn" onClick={onTip} className="ghost-btn py-3 text-xs tracking-[0.16em] uppercase flex items-center justify-center gap-2"><Gift className="w-4 h-4" /> Send a tip</button>
            {TIER_RANK[user.tier] < 3 && <Link to="/join" data-testid="feed-upgrade-btn" className="gold-btn py-3 text-xs font-semibold tracking-[0.16em] uppercase flex items-center justify-center gap-2"><Sparkles className="w-4 h-4" /> Upgrade</Link>}
          </div>
        )}
        {isAdmin && <Link to="/studio" data-testid="feed-studio-link" className="gold-btn mt-6 py-3 text-xs font-semibold tracking-[0.16em] uppercase flex items-center justify-center">Open studio</Link>}
      </div>
    </aside>
  );
}

export default function Feed() {
  const { user } = useAuth();
  const [posts, setPosts] = useState(null);
  const [filter, setFilter] = useState("all");
  const [tip, setTip] = useState({ open: false, postId: null });

  useEffect(() => {
    api.get("/posts").then((r) => setPosts(Array.isArray(r.data) ? r.data : [])).catch(() => setPosts([]));
  }, []);

  const shown = (posts || []).filter((p) => filter === "all" || (filter === "locked" ? p.locked : p.media_type === filter));

  return (
    <main data-testid="feed-page" className="max-w-[1200px] mx-auto px-4 sm:px-8 pt-[110px] pb-24 grid lg:grid-cols-[300px_1fr] gap-8 lg:gap-12">
      <ProfileCard user={user} onTip={() => setTip({ open: true, postId: null })} />
      <section>
        <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
          <h1 className="font-display text-4xl sm:text-5xl text-[#0E1B3D]">The <span className="italic text-[#D7263D]">feed</span></h1>
          <div className="flex gap-1 border border-[#0E1B3D]/10 p-1">
            {FILTERS.map(([k, l]) => (
              <button key={k} data-testid={`feed-filter-${k}`} onClick={() => setFilter(k)}
                className={`px-3 sm:px-4 py-2 text-xs tracking-[0.12em] uppercase transition-colors ${filter === k ? "bg-[#D7263D] text-[#FFF8EE]" : "text-[#26355A] hover:text-[#D7263D]"}`}>{l}</button>
            ))}
          </div>
        </div>
        {posts === null && <div className="h-96 border border-[#0E1B3D]/[0.08] animate-pulse bg-[#FFFFFF]" />}
        {posts && shown.length === 0 && <p data-testid="feed-empty" className="text-[#4A5878] py-20 text-center border border-[#0E1B3D]/[0.08]">Nothing here yet.</p>}
        <div className="space-y-8 max-w-[680px]">
          {shown.map((p) => (
            <PostCard key={p.id} post={p} isAdmin={user.role === "admin"} onTip={(id) => setTip({ open: true, postId: id })} onDeleted={(id) => setPosts((ps) => ps.filter((x) => x.id !== id))} />
          ))}
        </div>
      </section>
      <TipDialog open={tip.open} postId={tip.postId} onOpenChange={(o) => setTip((t) => ({ ...t, open: o }))} />
    </main>
  );
}
